import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import {
  MdCheckCircle,
  MdContentCopy,
  MdOpenInNew,
  MdPhoneIphone,
  MdPhotoLibrary,
  MdRefresh,
  MdOutlineAutoAwesome,
  MdOutlineWarningAmber,
  MdAutoFixHigh,
  MdDeleteOutline,
  MdClose,
} from "react-icons/md";
import { io } from "socket.io-client";
import {
  apiFetchShopMedia,
  apiCreateShootSession,
  apiEnhanceMedia,
  apiArchiveMedia,
} from "../api";
import { getTokenFromLocalStorage } from "../utilities/tokenUtils.js";

// Polling is a fallback; live updates arrive via Socket.IO.
const PRODUCT_SHOOT_POLL_INTERVAL_MS = 15000;

const isMobileBrowser = () => {
  if (typeof navigator === "undefined") return false;
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent || "");
};

const MobileProductShootPicker = ({
  vendorId,
  draftId,
  disabled = false,
  onSelectImage,
  // `compact` swaps the full gradient marketing strip for a single quiet
  // button. Shooting on a phone is the higher-quality route but it is NOT the
  // common one — most vendors just want to upload a file they already have,
  // and a large panel advertising a phone workflow next to a small "Replace
  // image" link made the ordinary path look like the secondary one. The modal,
  // the deep link, the QR and the live sync are all unchanged.
  compact = false,
}) => {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [copied, setCopied] = useState(false);
  const [qrFailed, setQrFailed] = useState(false);
  const [shootSession, setShootSession] = useState(null); // { token, expiresAt }
  const [sessionLoading, setSessionLoading] = useState(false);
  const socketRef = useRef(null);
  const isMobile = useMemo(isMobileBrowser, []);

  const deepLink = useMemo(() => {
    if (!vendorId) return "";
    const params = new URLSearchParams({ vendorId, source: "dashboard" });
    if (draftId) params.set("draftId", draftId);
    if (shootSession?.token) params.set("shootSession", shootSession.token);
    return `com.lusuku://shoot?${params.toString()}`;
  }, [draftId, vendorId, shootSession]);

  const qrCodeUrl = useMemo(() => {
    if (!deepLink) return "";
    return `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(
      deepLink
    )}`;
  }, [deepLink]);

  const ensureSession = useCallback(async () => {
    if (!vendorId) return null;
    if (shootSession?.token) {
      const notExpired =
        !shootSession.expiresAt ||
        new Date(shootSession.expiresAt).getTime() - Date.now() > 60 * 1000;
      if (notExpired) return shootSession;
    }
    setSessionLoading(true);
    try {
      const res = await apiCreateShootSession({ draftId: draftId || null });
      const data = res?.data?.data || res?.data || {};
      if (!data?.token) throw new Error("Server did not return a token.");
      const next = { token: data.token, expiresAt: data.expiresAt || null };
      setShootSession(next);
      return next;
    } catch (err) {
      console.error("Failed to create shoot session:", err);
      setError(
        err?.response?.data?.error?.message ||
          "Could not start a shoot session. Please try again."
      );
      return null;
    } finally {
      setSessionLoading(false);
    }
  }, [vendorId, draftId, shootSession]);

  const loadRecentShots = useCallback(
    async (refreshKey = Date.now()) => {
      if (!vendorId) return;
      try {
        setLoading(true);
        setError("");
        const response = await apiFetchShopMedia(
          vendorId,
          refreshKey,
          shootSession?.token
        );
        const media = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];
        setItems(media);
      } catch (fetchError) {
        console.error("Failed to fetch mobile product shots:", fetchError);
        setError(
          fetchError?.response?.data?.error?.message ||
            "We couldn't load your recent mobile shots yet."
        );
      } finally {
        setLoading(false);
      }
    },
    [vendorId, shootSession]
  );

  // Realtime: subscribe to the shootSession room for instant media:uploaded
  // events. Falls back to 15s polling if the socket drops.
  useEffect(() => {
    if (!show || !vendorId || !shootSession?.token) return undefined;

    const token = getTokenFromLocalStorage();
    const socket = io(process.env.REACT_APP_SERVER_URL, {
      auth: token ? { token } : undefined,
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;

    const join = () => {
      socket.emit(
        "joinShootSession",
        { token: shootSession.token },
        (ack) => {
          if (!ack?.ok) {
            console.warn("joinShootSession rejected:", ack?.error);
          }
        }
      );
    };

    socket.on("connect", join);
    socket.on("media:uploaded", (media) => {
      if (!media?._id) return;
      setItems((prev) => {
        if (prev.some((m) => m._id === media._id)) return prev;
        return [media, ...prev];
      });
    });

    return () => {
      socket.emit("leaveShootSession", {
        vendorId,
        draftId: draftId || null,
      });
      socket.off("media:uploaded");
      socket.disconnect();
      socketRef.current = null;
    };
  }, [show, vendorId, draftId, shootSession]);

  // Low-frequency polling fallback in case the socket is blocked.
  useEffect(() => {
    if (!show || !vendorId) return undefined;
    const intervalId = window.setInterval(() => {
      loadRecentShots(Date.now());
    }, PRODUCT_SHOOT_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [loadRecentShots, show, vendorId]);

  // Silently refresh the shoot session 5 minutes before it expires so a
  // long-running shoot never hits INVALID_SESSION mid-upload.
  useEffect(() => {
    if (!show || !shootSession?.expiresAt) return undefined;
    const msUntilRefresh = Math.max(
      5_000,
      new Date(shootSession.expiresAt).getTime() - Date.now() - 5 * 60 * 1000
    );
    const timerId = window.setTimeout(async () => {
      try {
        const res = await apiCreateShootSession({ draftId: draftId || null });
        const data = res?.data?.data || res?.data || {};
        if (data?.token) {
          setShootSession({
            token: data.token,
            expiresAt: data.expiresAt || null,
          });
        }
      } catch (err) {
        console.warn("Silent shoot-session refresh failed:", err?.message);
      }
    }, msUntilRefresh);
    return () => window.clearTimeout(timerId);
  }, [show, draftId, shootSession]);

  const openModal = async () => {
    setShow(true);
    setCopied(false);
    const session = await ensureSession();
    // Load shots AFTER session exists so the request is authorised even when
    // the dashboard user is not the same account as the mobile shooter.
    if (session) {
      await loadRecentShots(Date.now());
    }
  };

  const launchApp = () => {
    if (!deepLink) return;
    window.location.href = deepLink;
  };

  const copyLink = async () => {
    if (!deepLink || !navigator?.clipboard?.writeText) return;
    await navigator.clipboard.writeText(deepLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  // ── Review sub-modal state ───────────────────────────────────────────────
  //
  // The "Needs review" badge used to be cosmetic — clicking a tile sent the
  // image straight to the listing regardless of its quality flag. Now, tiles
  // flagged amazonReady:false open a review modal where the vendor can (a)
  // clean the background server-side via Remove.bg, (b) use the image as-is,
  // or (c) discard (archive) it. Lusuku-ready tiles still select directly.
  const [reviewItem, setReviewItem] = useState(null);
  const [reviewBusy, setReviewBusy] = useState(null); // 'enhance' | 'discard' | null
  const [reviewError, setReviewError] = useState("");

  // Pick the best URL to hand to the listing form: padded 2000x2000 white
  // canvas produced by the backend (displayUrl). Falls back to the raw
  // Cloudinary URL for older records that predate the displayUrl field.
  const pickFinalUrl = (item) => item?.displayUrl || item?.cloudinaryUrl;

  const finaliseSelect = (item) => {
    onSelectImage?.(pickFinalUrl(item), item);
    setShow(false);
    setReviewItem(null);
  };

  const handleSelect = (item) => {
    if (!item) return;
    if (item.amazonReady) {
      finaliseSelect(item);
      return;
    }
    setReviewError("");
    setReviewItem(item);
  };

  const handleReviewEnhance = useCallback(async () => {
    if (!reviewItem?._id) return;
    setReviewBusy("enhance");
    setReviewError("");
    try {
      const res = await apiEnhanceMedia(reviewItem._id, {
        shootSession: shootSession?.token || undefined,
      });
      const enhanced = res?.data?.data || res?.data;
      if (!enhanced?._id) throw new Error("Unexpected response");
      // Swap the item in the list and in the review modal so the vendor
      // sees the cleaned version before committing.
      setItems((prev) =>
        prev.map((m) => (m._id === enhanced._id ? enhanced : m))
      );
      setReviewItem(enhanced);
    } catch (err) {
      const apiError = err?.response?.data?.error || {};
      // When Remove.bg rejects the call, the backend now forwards upstream
      // status + body so we can tell the vendor *why* (invalid API key,
      // exhausted credits, unknown image format, …).
      const upstream = apiError.upstreamBody;
      const upstreamDetail =
        upstream && typeof upstream === "object"
          ? upstream?.errors?.[0]?.title ||
            upstream?.errors?.[0]?.detail ||
            JSON.stringify(upstream).slice(0, 200)
          : typeof upstream === "string"
            ? upstream
            : "";
      const baseMessage =
        apiError.message ||
          err?.message ||
          "Background cleaning failed. Please try again.";
      setReviewError(
        apiError.upstreamStatus
          ? `${baseMessage} (upstream ${apiError.upstreamStatus}${
              upstreamDetail ? `: ${upstreamDetail}` : ""
            })`
          : baseMessage
      );
    } finally {
      setReviewBusy(null);
    }
  }, [reviewItem, shootSession]);

  const handleReviewDiscard = useCallback(async () => {
    if (!reviewItem?._id) return;
    setReviewBusy("discard");
    setReviewError("");
    try {
      await apiArchiveMedia(reviewItem._id);
      setItems((prev) => prev.filter((m) => m._id !== reviewItem._id));
      setReviewItem(null);
    } catch (err) {
      setReviewError(
        err?.response?.data?.error?.message ||
          err?.message ||
          "Couldn't discard this image."
      );
    } finally {
      setReviewBusy(null);
    }
  }, [reviewItem]);

  return (
    <>
      {/* ── Trigger ──────────────────────────────────────────────────────────
          Two presentations of the same action. `compact` is what the product
          forms use: one outline button that sits alongside the device-upload
          control as a peer, not above it as a headline. */}
      {compact ? (
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={openModal}
          disabled={disabled || !vendorId}
          title="Shoot the product on your phone and pick the saved shot here"
          style={{
            borderRadius: 8,
            fontSize: "0.8rem",
            fontWeight: 600,
            padding: "7px 14px",
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            whiteSpace: "nowrap",
          }}
        >
          <MdPhoneIphone size={16} />
          Shoot on phone
        </Button>
      ) : (
      <div
        style={{
          marginTop: 18,
          borderRadius: 14,
          border: "1.5px solid #bfdbfe",
          background: "linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)",
          overflow: "hidden",
        }}
      >
        {/* Top label bar */}
        <div
          style={{
            background: "rgba(10,87,139,0.07)",
            borderBottom: "1px solid #dbeafe",
            padding: "7px 16px",
            display: "flex",
            alignItems: "center",
            gap: 7,
          }}
        >
          <MdPhoneIphone size={15} color="#0a578b" />
          <span
            style={{
              fontWeight: 700,
              fontSize: "0.76rem",
              color: "#0a578b",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Mobile product shoot
          </span>
          <span
            style={{
              marginLeft: "auto",
              fontSize: "0.70rem",
              color: "#64748b",
              fontStyle: "italic",
            }}
          >
            Best quality — uses your phone&apos;s camera optics
          </span>
        </div>

        {/* Body row */}
        <div
          style={{
            padding: "14px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 200 }}>
            <p
              style={{
                margin: 0,
                fontSize: "0.82rem",
                color: "#374151",
                lineHeight: 1.6,
              }}
            >
              Open Lusuku on your phone, shoot the product, and pick the saved
              shot here — the image lands directly in this listing.
            </p>
            <div
              style={{
                marginTop: 8,
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              {[
                { icon: "fas fa-camera", text: "Guided framing" },
                { icon: "fas fa-magic", text: "Auto white background" },
                { icon: "fas fa-bolt", text: "Live sync" },
              ].map(({ icon, text }) => (
                <span
                  key={text}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    fontSize: "0.71rem",
                    color: "#0a578b",
                    background: "#dbeafe",
                    borderRadius: 999,
                    padding: "3px 9px",
                    fontWeight: 600,
                  }}
                >
                  <i className={icon} style={{ fontSize: "0.65rem" }} />
                  {text}
                </span>
              ))}
            </div>
          </div>

          <Button
            onClick={openModal}
            disabled={disabled || !vendorId}
            style={{
              background: "#17385a",
              borderColor: "#17385a",
              borderRadius: 10,
              padding: "10px 20px",
              fontWeight: 700,
              fontSize: "0.88rem",
              whiteSpace: "nowrap",
              boxShadow: "0 3px 10px rgba(23,56,90,0.22)",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#0a578b";
              e.currentTarget.style.boxShadow = "0 5px 14px rgba(10,87,139,0.30)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#17385a";
              e.currentTarget.style.boxShadow = "0 3px 10px rgba(23,56,90,0.22)";
            }}
          >
            <MdOpenInNew size={16} style={{ marginRight: 7, marginTop: -2 }} />
            Shoot &amp; pick image
          </Button>
        </div>
      </div>
      )}

      <Modal
        show={show}
        onHide={() => setShow(false)}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          style={{
            background: "linear-gradient(135deg, #0a578b 0%, #17385a 100%)",
            borderBottom: "none",
            padding: "18px 24px",
          }}
        >
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 10 }}
          >
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                background: "rgba(255,255,255,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MdPhoneIphone size={20} />
            </div>
            Shoot product photo on mobile
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 22, background: "#f8fafc" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(220px, 250px) 1fr",
              gap: 18,
              alignItems: "start",
            }}
          >
            {/* ── QR / link panel ──────────────────────────────── */}
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                padding: 16,
                background: "#ffffff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              {/* QR code */}
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                  padding: 10,
                  marginBottom: 14,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 200,
                }}
              >
                {!qrFailed && qrCodeUrl ? (
                  <img
                    src={qrCodeUrl}
                    alt="Open mobile shoot flow"
                    style={{
                      width: "100%",
                      maxWidth: 200,
                      height: "auto",
                      display: "block",
                      borderRadius: 8,
                    }}
                    onError={() => setQrFailed(true)}
                  />
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      color: "#94a3b8",
                      fontSize: "0.80rem",
                      lineHeight: 1.5,
                      padding: "0 8px",
                    }}
                  >
                    <MdPhoneIphone size={32} style={{ marginBottom: 8, opacity: 0.5 }} />
                    <br />
                    QR unavailable. Use the button below or copy the link to your phone.
                  </div>
                )}
              </div>

              {/* CTA — open app or scan instruction */}
              {isMobile ? (
                <Button
                  onClick={launchApp}
                  disabled={!deepLink || sessionLoading}
                  style={{
                    width: "100%",
                    background: "#17385a",
                    borderColor: "#17385a",
                    borderRadius: 9,
                    fontWeight: 700,
                    fontSize: "0.86rem",
                    boxShadow: "0 3px 8px rgba(23,56,90,0.2)",
                  }}
                >
                  <MdOpenInNew size={15} style={{ marginRight: 6, marginTop: -2 }} />
                  Open Lusuku app
                </Button>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "9px 12px",
                    background: "#f0f7ff",
                    color: "#1e40af",
                    borderRadius: 9,
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    lineHeight: 1.5,
                    border: "1px solid #bfdbfe",
                  }}
                >
                  📷&nbsp; Scan with your phone&apos;s camera to open Lusuku
                </div>
              )}

              {/* Copy link + refresh row */}
              <div style={{ marginTop: 10, display: "flex", gap: 7, flexWrap: "wrap" }}>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={copyLink}
                  disabled={!deepLink || !navigator?.clipboard?.writeText}
                  style={{ borderRadius: 8, fontWeight: 600, flex: 1, fontSize: "0.78rem" }}
                >
                  <MdContentCopy size={13} style={{ marginRight: 5 }} />
                  {copied ? "Copied ✓" : "Copy link"}
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => loadRecentShots(Date.now())}
                  disabled={loading}
                  style={{ borderRadius: 8, fontWeight: 600, flex: 1, fontSize: "0.78rem" }}
                >
                  <MdRefresh size={13} style={{ marginRight: 5 }} />
                  Refresh
                </Button>
              </div>

              {/* Session status pill */}
              <div style={{ marginTop: 10, textAlign: "center" }}>
                {sessionLoading ? (
                  <span style={{ fontSize: "0.72rem", color: "#64748b" }}>
                    <Spinner animation="border" size="sm" style={{ marginRight: 5 }} />
                    Preparing session…
                  </span>
                ) : shootSession?.token ? (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: "0.72rem",
                      color: "#16a34a",
                      fontWeight: 700,
                      background: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                      borderRadius: 999,
                      padding: "3px 10px",
                    }}
                  >
                    ● Session ready
                  </span>
                ) : null}
              </div>
            </div>

            {/* ── Right column: instructions + shot grid ────────── */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

              {/* How it works */}
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 14,
                  padding: "14px 16px",
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    color: "#17385a",
                    fontSize: "0.88rem",
                    marginBottom: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                  }}
                >
                  <span style={{ fontSize: "1rem" }}>📋</span>
                  How it works
                </div>
                {[
                  { step: "1", text: <>Scan the QR with your phone&apos;s camera (or tap <em>Open Lusuku app</em> on a phone). Any Lusuku account works.</> },
                  { step: "2", text: "Shoot or pick one or more product images in the app and tap Done." },
                  { step: "3", text: "Come back here — new shots appear below in real time for one-click selection." },
                ].map(({ step, text }) => (
                  <div
                    key={step}
                    style={{
                      display: "flex",
                      gap: 10,
                      marginBottom: step === "3" ? 0 : 8,
                      alignItems: "flex-start",
                    }}
                  >
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: "#17385a",
                        color: "#fff",
                        fontSize: "0.70rem",
                        fontWeight: 800,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        marginTop: 1,
                      }}
                    >
                      {step}
                    </div>
                    <span style={{ fontSize: "0.80rem", color: "#374151", lineHeight: 1.55 }}>{text}</span>
                  </div>
                ))}
              </div>

              {/* Error */}
              {error ? (
                <Alert variant="warning" style={{ marginBottom: 0, fontSize: "0.82rem", borderRadius: 10 }}>
                  {error}
                </Alert>
              ) : null}

              {/* Recent shots header */}
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 14,
                  overflow: "hidden",
                }}
              >
                {/* Section header */}
                <div
                  style={{
                    padding: "10px 14px",
                    borderBottom: "1px solid #e2e8f0",
                    background: "#f8fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: "#17385a",
                      fontSize: "0.82rem",
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                    }}
                  >
                    <MdPhotoLibrary size={16} />
                    Recent mobile shots
                  </div>
                  {loading ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#64748b", fontSize: "0.76rem" }}>
                      <Spinner animation="border" size="sm" />
                      Loading…
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        fontSize: "0.72rem",
                        color: "#16a34a",
                        fontWeight: 700,
                        background: "#f0fdf4",
                        border: "1px solid #bbf7d0",
                        borderRadius: 999,
                        padding: "2px 9px",
                      }}
                    >
                      ● Live
                    </div>
                  )}
                </div>

                {/* Grid or empty state */}
                <div style={{ padding: 12 }}>
                  {items.length === 0 && !loading ? (
                    <div
                      style={{
                        border: "1.5px dashed #cbd5e1",
                        borderRadius: 12,
                        padding: "22px 16px",
                        textAlign: "center",
                        color: "#94a3b8",
                        background: "#f8fafc",
                      }}
                    >
                      <MdPhotoLibrary size={28} style={{ opacity: 0.35, marginBottom: 8 }} />
                      <p style={{ margin: 0, fontSize: "0.80rem", lineHeight: 1.5 }}>
                        No mobile shots yet.
                        <br />
                        Save one in the app and it will appear here.
                      </p>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(128px, 1fr))",
                        gap: 10,
                      }}
                    >
                      {items.map((item) => (
                        <button
                          key={item._id}
                          type="button"
                          onClick={() => handleSelect(item)}
                          style={{
                            border: `2px solid ${item.amazonReady ? "#bbf7d0" : "#fde68a"}`,
                            background: "#fff",
                            borderRadius: 12,
                            padding: 8,
                            textAlign: "left",
                            cursor: "pointer",
                            transition: "box-shadow 0.15s, transform 0.15s",
                            position: "relative",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.12)";
                            e.currentTarget.style.transform = "translateY(-2px)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.transform = "translateY(0)";
                          }}
                        >
                          {/* Thumbnail */}
                          <div style={{ position: "relative" }}>
                            <img
                              src={item.thumbnailUrl || item.cloudinaryUrl}
                              alt="Recent mobile shot"
                              style={{
                                width: "100%",
                                aspectRatio: "1 / 1",
                                objectFit: "cover",
                                borderRadius: 8,
                                display: "block",
                                marginBottom: 7,
                                background: "#f1f5f9",
                              }}
                            />
                            {/* Ready badge overlay */}
                            <div
                              style={{
                                position: "absolute",
                                top: 5,
                                right: 5,
                                width: 20,
                                height: 20,
                                borderRadius: "50%",
                                background: item.amazonReady ? "#16a34a" : "#f59e0b",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                              }}
                            >
                              {item.amazonReady ? (
                                <MdCheckCircle size={13} color="#fff" />
                              ) : (
                                <MdOutlineWarningAmber size={13} color="#fff" />
                              )}
                            </div>
                          </div>

                          {/* Label */}
                          <div
                            style={{
                              fontSize: "0.72rem",
                              color: "#17385a",
                              fontWeight: 700,
                              marginBottom: 5,
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            {item.amazonReady ? <MdCheckCircle size={12} color="#16a34a" /> : <MdOutlineWarningAmber size={12} color="#d97706" />}
                            {item.amazonReady ? "Use this" : "Review"}
                          </div>

                          {/* Pill badge */}
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              padding: "3px 7px",
                              borderRadius: 999,
                              background: item.amazonReady ? "rgba(22,163,74,0.10)" : "rgba(245,158,11,0.12)",
                              color: item.amazonReady ? "#166534" : "#92400e",
                              fontSize: "0.68rem",
                              fontWeight: 700,
                            }}
                          >
                            {item.amazonReady ? <MdOutlineAutoAwesome size={11} /> : <MdOutlineWarningAmber size={11} />}
                            {item.amazonReady ? "Lusuku ready" : "Needs review"}
                          </div>

                          {/* Fill % */}
                          <div style={{ fontSize: "0.68rem", color: "#94a3b8", marginTop: 4, fontWeight: 600 }}>
                            {Math.round(item.fillPercent || 0)}% fill
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* ── Review sub-modal for 'Needs review' tiles ────────────────────── */}
      <Modal
        show={Boolean(reviewItem)}
        onHide={() => {
          if (reviewBusy) return;
          setReviewItem(null);
          setReviewError("");
        }}
        centered
        size="md"
      >
        <Modal.Header
          closeButton
          style={{
            background: "#17385a",
            color: "#fff",
            borderBottom: 0,
          }}
        >
          <Modal.Title>
            Review this shot
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 20 }}>
          {reviewItem ? (
            <>
              <div
                style={{
                  width: "100%",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                  padding: 8,
                  marginBottom: 14,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <img
                  src={
                    reviewItem.displayUrl ||
                    reviewItem.thumbnailUrl ||
                    reviewItem.cloudinaryUrl
                  }
                  alt="Review preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 320,
                    objectFit: "contain",
                    borderRadius: 8,
                    background: "#fff",
                  }}
                />
              </div>

              <div
                style={{
                  fontSize: "0.85rem",
                  color: "#334155",
                  lineHeight: 1.5,
                  marginBottom: 14,
                }}
              >
                {reviewItem.amazonReady ? (
                  <span style={{ color: "#166534", fontWeight: 600 }}>
                    ✓ Background is clean — ready to use.
                  </span>
                ) : (
                  <>
                    This image was flagged because the background isn&apos;t a
                    clean white. You can clean it server-side, use it as-is if
                    the context suits your listing, or discard and shoot
                    again.
                  </>
                )}
              </div>

              {reviewError ? (
                <Alert variant="warning" style={{ marginBottom: 12 }}>
                  {reviewError}
                </Alert>
              ) : null}

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                {!reviewItem.amazonReady ? (
                  <Button
                    onClick={handleReviewEnhance}
                    disabled={Boolean(reviewBusy)}
                    style={{
                      background: "#17385a",
                      borderColor: "#17385a",
                      fontWeight: 700,
                    }}
                  >
                    {reviewBusy === "enhance" ? (
                      <>
                        <Spinner animation="border" size="sm" /> Cleaning
                        background&hellip;
                      </>
                    ) : (
                      <>
                        <MdAutoFixHigh
                          size={18}
                          style={{ marginRight: 6, marginTop: -2 }}
                        />
                        Clean background (server)
                      </>
                    )}
                  </Button>
                ) : null}

                <Button
                  variant="outline-primary"
                  onClick={() => finaliseSelect(reviewItem)}
                  disabled={Boolean(reviewBusy)}
                  style={{ fontWeight: 700 }}
                >
                  <MdCheckCircle
                    size={18}
                    style={{ marginRight: 6, marginTop: -2 }}
                  />
                  Use this image
                </Button>

                <Button
                  variant="outline-danger"
                  onClick={handleReviewDiscard}
                  disabled={Boolean(reviewBusy)}
                  style={{ fontWeight: 700 }}
                >
                  {reviewBusy === "discard" ? (
                    <>
                      <Spinner animation="border" size="sm" /> Discarding&hellip;
                    </>
                  ) : (
                    <>
                      <MdDeleteOutline
                        size={18}
                        style={{ marginRight: 6, marginTop: -2 }}
                      />
                      Discard
                    </>
                  )}
                </Button>

                <Button
                  variant="link"
                  onClick={() => {
                    if (reviewBusy) return;
                    setReviewItem(null);
                    setReviewError("");
                  }}
                  disabled={Boolean(reviewBusy)}
                  style={{ color: "#64748b", fontWeight: 600 }}
                >
                  <MdClose size={16} style={{ marginRight: 4 }} />
                  Cancel
                </Button>
              </div>
            </>
          ) : null}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default MobileProductShootPicker;
